import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  LinearProgress,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { api } from '../../services/api';

interface QuizOption {
  id: number;
  optionText: string;
  displayOrder: number;
}

interface QuizQuestion {
  id: number;
  questionText: string;
  questionType: string;
  displayOrder: number;
  options: QuizOption[];
}

interface QuizDTO {
  id: number;
  title: string;
  timeLimitMinutes: number | null;
  minPassPercent: number | null;
  questions: QuizQuestion[];
}

interface AttemptResult {
  attemptId: number;
  score: number;
  correctAnswers: number;
  totalQuestions: number;
  passed: boolean;
  topicStatusAfter: string;
}

interface Props {
  courseId: number;
  nodeId: number;
}

export default function StudentTutorQuizSection({ courseId, nodeId }: Props) {
  const [quiz, setQuiz] = useState<QuizDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [noQuiz, setNoQuiz] = useState(false);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<AttemptResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ data: QuizDTO }>(`/api/student/courses/${courseId}/topics/${nodeId}/quiz`)
      .then(r => setQuiz(r.data.data))
      .catch(e => {
        if (e?.response?.status === 404) {
          setNoQuiz(true);
        } else {
          setError('Failed to load quiz');
        }
      })
      .finally(() => setLoading(false));
  }, [courseId, nodeId]);

  const handleSubmit = async () => {
    if (!quiz) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        quizId: quiz.id,
        answers: Object.entries(answers).map(([questionId, selectedOptionId]) => ({
          questionId: Number(questionId),
          selectedOptionId,
        })),
      };
      const res = await api.post<{ data: AttemptResult }>(
        `/api/student/courses/${courseId}/topics/${nodeId}/quiz/attempt`,
        payload
      );
      setResult(res.data.data);
    } catch {
      setError('Failed to submit quiz. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = () => {
    setResult(null);
    setAnswers({});
  };

  if (loading) return <CircularProgress size={20} />;
  if (noQuiz) return null;
  if (error && !quiz) return <Alert severity="error">{error}</Alert>;
  if (!quiz) return null;

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        {quiz.title}
      </Typography>
      {quiz.minPassPercent && (
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          Pass mark: {quiz.minPassPercent}%
        </Typography>
      )}

      {result ? (
        <Box>
          <Alert severity={result.passed ? 'success' : 'warning'} sx={{ mb: 2 }}>
            <strong>{result.passed ? 'Passed!' : 'Not quite!'}</strong>{' '}
            Score: {result.score}% ({result.correctAnswers}/{result.totalQuestions} correct)
          </Alert>
          <LinearProgress
            variant="determinate"
            value={result.score}
            color={result.passed ? 'success' : 'warning'}
            sx={{ mb: 2 }}
          />
          <Button variant="outlined" size="small" onClick={handleRetry}>
            Try again
          </Button>
        </Box>
      ) : (
        <Box>
          {quiz.questions.map((q, qi) => (
            <Box key={q.id} mb={3}>
              <Typography variant="body2" fontWeight="medium" mb={1}>
                {qi + 1}. {q.questionText}
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  value={answers[q.id] ?? ''}
                  onChange={(e) => setAnswers(prev => ({ ...prev, [q.id]: Number(e.target.value) }))}
                >
                  {q.options.map(o => (
                    <FormControlLabel
                      key={o.id}
                      value={o.id}
                      control={<Radio size="small" />}
                      label={o.optionText}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>
          ))}
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || Object.keys(answers).length === 0}
          >
            {submitting ? <CircularProgress size={18} /> : 'Submit answers'}
          </Button>
        </Box>
      )}
    </Box>
  );
}
